import { AddressAutocomplete } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { useAddressSource } from "../../../../lib/address";
import type { CompanyInfo } from "../../../../lib/sales";
import { Card, CardHead } from "../../../ui";
import { AreaField, Field, TextField } from "./fields";

/**
 * Settings -> Invoices -> seller details: the party the document is issued by,
 * as it prints in the PDF header and in the payment block.
 *
 * Every field reports up rather than saving on its own - the tab has one Save
 * button and it belongs to `InvoiceSettings`.
 */
export function SellerCard({
  company,
  onChange,
}: {
  company: CompanyInfo;
  onChange: (patch: Partial<CompanyInfo>) => void;
}) {
  const t = useT();
  const addressSource = useAddressSource();

  return (
    <Card>
      <CardHead title={t("settings.invoicing.seller")} sub={t("settings.invoicing.sellerDesc")} />

      {/* A surface has no padding of its own; a body that is not `crm-rows`
          brings its own. */}
      <div className="grid gap-x-5 px-6 pb-6 pt-1 sm:grid-cols-2">
        <TextField
          className="sm:col-span-2"
          label={t("settings.company.name")}
          value={company.name}
          onChange={(v) => onChange({ name: v })}
        />
        <TextField
          label={t("settings.company.regCode")}
          value={company.regCode}
          onChange={(v) => onChange({ regCode: v })}
          numeric
        />
        <TextField
          label={t("settings.company.vatNumber")}
          value={company.vatNumber}
          onChange={(v) => onChange({ vatNumber: v })}
        />
        <Field className="sm:col-span-2" label={t("settings.company.address")}>
          {/* Still a free text box that keeps line breaks; a picked address fills one line. */}
          <AddressAutocomplete
            multiline
            rows={3}
            source={addressSource}
            value={company.address}
            onChange={(v) => onChange({ address: v })}
            placeholder={t("settings.company.addressPlaceholder")}
            aria-label={t("settings.company.address")}
          />
        </Field>
        <TextField
          label={t("settings.company.email")}
          type="email"
          inputMode="email"
          value={company.email}
          onChange={(v) => onChange({ email: v })}
        />
        <TextField
          label={t("settings.company.phone")}
          inputMode="tel"
          value={company.phone}
          onChange={(v) => onChange({ phone: v })}
        />
        <TextField
          label={t("settings.company.website")}
          inputMode="url"
          value={company.website}
          onChange={(v) => onChange({ website: v })}
        />
        <TextField
          label={t("settings.company.bankName")}
          value={company.bankName}
          onChange={(v) => onChange({ bankName: v })}
        />
        <TextField
          className="sm:col-span-2"
          label={t("settings.company.iban")}
          value={company.iban}
          onChange={(v) => onChange({ iban: v })}
          numeric
        />
        <AreaField
          className="sm:col-span-2"
          label={t("settings.company.footerNote")}
          value={company.footerNote}
          onChange={(v) => onChange({ footerNote: v })}
          placeholder={t("settings.company.footerNotePlaceholder")}
        />
      </div>
    </Card>
  );
}
